// src/components/homeComponents/FinalCTA.jsx
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaInstagram, FaTiktok, FaArrowRight } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

const STATS = [
  { value: 50, suffix: "M+", label: "views generated" },
  { value: 20, suffix: "+", label: "brands shipped" },
  { value: 3, suffix: "", label: "markets, one studio" },
];

const FinalCTA = () => {
  const sectionRef = useRef(null);
  const statRefs = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      STATS.forEach((s, i) => {
        const node = statRefs.current[i];
        if (!node) return;
        const obj = { v: 0 };
        ScrollTrigger.create({
          trigger: node,
          start: "top 85%",
          once: true,
          onEnter: () =>
            gsap.to(obj, {
              v: s.value,
              duration: 1.6,
              ease: "power3.out",
              onUpdate: () => {
                node.textContent = `${Math.round(obj.v)}${s.suffix}`;
              },
            }),
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef}>
      <div className="aura" />

      <div className="stats">
        {STATS.map((s, i) => (
          <div className="stat" key={i}>
            <span className="value" ref={(el) => (statRefs.current[i] = el)}>
              0{s.suffix}
            </span>
            <span className="label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="cta">
        <span className="kicker">LAST FRAME</span>
        <h2>
          Let's build your <em>obsession.</em>
        </h2>
        <p>
          One studio for the content, the brand, and the website behind it.
        </p>
        <div className="btns">
          <button className="primary">
            Start a project <FaArrowRight />
          </button>
          <button className="ghost">See more work</button>
        </div>
      </div>

      <footer>
        <div className="brand">
          <h3>ASites</h3>
          <p>Web · Content · Marketing — Kosovo, Albania, Macedonia.</p>
        </div>
        <div className="links">
          <a href="#work">Work</a>
          <a href="#process">Process</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="social">
          <a href="https://instagram.com" aria-label="Instagram">
            <FaInstagram />
          </a>
          <a href="https://tiktok.com" aria-label="TikTok">
            <FaTiktok />
          </a>
        </div>
      </footer>
      <div className="copy">
        © {new Date().getFullYear()} ASites Agency — Built obsessively.
      </div>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: radial-gradient(
    circle at 50% 0%,
    #2a1d40 0%,
    #120818 40%,
    #08040c 70%
  );
  padding: 8em 7% 2.5em;
  overflow: hidden;

  .aura {
    position: absolute;
    top: -25%;
    left: 50%;
    transform: translateX(-50%);
    width: 70vw;
    height: 70vw;
    max-width: 900px;
    max-height: 900px;
    background: radial-gradient(circle, #5a4a8a55, transparent 60%);
    filter: blur(40px);
    pointer-events: none;
  }

  .stats {
    position: relative;
    display: flex;
    justify-content: center;
    gap: clamp(2em, 8vw, 7em);
    flex-wrap: wrap;
    padding-bottom: 5em;
    border-bottom: 1px solid #1d1530;
    .stat {
      text-align: center;
      .value {
        display: block;
        font-family: "Readex Pro", sans-serif;
        font-weight: 200;
        font-size: clamp(2.6rem, 6vw, 5rem);
        line-height: 1;
        background: linear-gradient(180deg, #ffffff, #b9c5fb);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
      .label {
        color: #8a8595;
        font-size: 0.9rem;
        letter-spacing: 0.05em;
      }
    }
  }

  .cta {
    position: relative;
    text-align: center;
    padding: 5em 0 6em;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(2.4rem, 7vw, 6rem);
      line-height: 1;
      margin: 0.3em 0 0.4em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
    p {
      color: #b6b6b6;
      max-width: 40ch;
      margin: 0 auto 2em;
    }
    .btns {
      display: flex;
      justify-content: center;
      gap: 0.8em;
      flex-wrap: wrap;
      button {
        font-family: "Montserrat";
        font-size: 1rem;
        padding: 16px 30px;
        border-radius: 30px;
        cursor: pointer;
        transition: 0.35s;
        display: inline-flex;
        align-items: center;
        gap: 0.6em;
      }
      .primary {
        background: linear-gradient(120deg, #9281dd, #b9c5fb);
        color: #100a18;
        border: none;
        font-weight: 600;
        &:hover {
          box-shadow: 0 0 30px -4px #9281dd;
          transform: translateY(-2px);
        }
      }
      .ghost {
        background: none;
        color: #fff;
        border: 1px solid #463d6e;
        &:hover {
          background: #ffffff0d;
        }
      }
    }
  }

  footer {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1.5em;
    padding: 2.5em 0;
    border-top: 1px solid #1d1530;
    .brand h3 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 300;
      font-size: 1.6rem;
    }
    .brand p {
      color: #6f6a7c;
      font-size: 0.85rem;
      margin-top: 0.3em;
    }
    .links {
      display: flex;
      gap: 1.8em;
      a {
        color: #cfcad9;
        text-decoration: none;
        font-size: 0.95rem;
        transition: 0.3s;
        &:hover {
          color: #9281dd;
        }
      }
    }
    .social {
      display: flex;
      gap: 1em;
      a {
        color: #cfcad9;
        font-size: 1.3rem;
        transition: 0.3s;
        &:hover {
          color: #9281dd;
          transform: translateY(-2px);
        }
      }
    }
  }

  .copy {
    position: relative;
    text-align: center;
    color: #4f4a5c;
    font-size: 0.8rem;
    padding-top: 1.5em;
  }
`;

export default FinalCTA;
